import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { MapPin, RefreshCw } from 'lucide-react'
import { shopsApi } from '../../lib/api'
import { LeafletMap, type MapPin as Pin } from '../../components/shared/LeafletMap'
import { tx } from '../../lib/i18n'

const PLAN_COLOR: Record<string, string> = { business: '#7c3aed', pro: '#4f46e5', starter: '#0ea5e9' }

/** Every shop branch with coordinates on one map; shops without a pin listed below. */
const ShopsMapPage: React.FC = () => {
  const { data, isLoading, refetch, isFetching } = useQuery({ queryKey: ['shops-map'], queryFn: shopsApi.map })
  const [plan, setPlan] = useState<string>('')
  const [q, setQ] = useState('')

  const pins = useMemo<Pin[]>(() => {
    const rows = (data?.pins ?? []).filter((p) => (!plan || p.planCode === plan) && (!q || p.shopName.toLowerCase().includes(q.toLowerCase()) || p.branchName.toLowerCase().includes(q.toLowerCase())))
    return rows.map((p) => ({
      id: p.branchId,
      lat: p.latitude,
      lng: p.longitude,
      title: `${p.shopName}${p.branchName !== p.shopName ? ' · ' + p.branchName : ''}`,
      color: p.shopStatus !== 'active' ? '#9ca3af' : PLAN_COLOR[p.planCode ?? ''] ?? '#4f46e5',
      html: `<b>${p.shopName}</b><br/>${p.branchName}${p.isMain ? ' (สาขาหลัก)' : ''}<br/>${p.address ?? ''}<br/>${p.phone ?? ''}<br/>แพ็กเกจ: ${p.planCode ?? '-'} · ${p.subStatus ?? '-'}<br/><a href="/shops/${p.shopId}">เปิดหน้าร้าน →</a>`,
    }))
  }, [data, plan, q])

  return (
    <div className="space-y-5" data-testid="shops-map-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><MapPin className="w-5 h-5" /> {tx('Shops map')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tx('Where every shop on Queza is — pins come from the branch coordinates shops set in their backoffice')}</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tx('Search shop...')} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="shops-map-search" />
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="shops-map-plan">
            <option value="">{tx('All plans')}</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="business">Business</option>
          </select>
          <button onClick={() => refetch()} disabled={isFetching} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"><RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} /> {tx('Refresh')}</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="shops-map-totals">
        <Stat label={tx('Shops')} value={data?.totals.shops ?? 0} />
        <Stat label={tx('Pinned')} value={data?.totals.pinned ?? 0} />
        <Stat label={tx('Not pinned')} value={(data?.totals.shops ?? 0) - (data?.totals.pinned ?? 0)} />
        <Stat label={tx('Branches on map')} value={pins.length} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
        {isLoading ? <div className="h-[520px] animate-pulse bg-gray-100 rounded-lg" /> : <LeafletMap pins={pins} height={520} testId="shops-map" />}
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
          <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ background: PLAN_COLOR.business }} />Business</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ background: PLAN_COLOR.pro }} />Pro</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ background: PLAN_COLOR.starter }} />Starter</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1 bg-gray-400" />{tx('Suspended / inactive')}</span>
        </div>
      </div>

      {data && data.unpinned.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-testid="shops-map-unpinned">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">{tx('Shops without a pin')} ({data.unpinned.length})</h2>
          <p className="text-xs text-gray-500 mb-3">{tx('The shop pins its branches itself (Backoffice → Branches → Edit → map). Ask them, or set it for them from the shop page.')}</p>
          <div className="flex flex-wrap gap-2">
            {data.unpinned.map((s) => (
              <Link key={s.id} to={`/shops/${s.id}`} className="text-xs px-2.5 py-1 rounded-full border border-gray-200 hover:bg-gray-50">{s.name}</Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-xl font-semibold text-gray-900">{value.toLocaleString('th-TH')}</div>
  </div>
)

export default ShopsMapPage
